/* eslint-disable */
import React, { useState } from 'react'
import { Input, Button, Card, List, Table, Tag, Spin, Empty, Descriptions, message } from 'antd'
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import Layout from '../Home'
import { getUserInfoByCid, getVisitsByPatientId } from '../../apiServices/patientLabDebugServices'
import {
  processVisits,
  groupLabResults,
  dedupeLabRows,
  checkResultInRange,
  formatVisitDateTime,
} from './labUtils'

const IN_RANGE = '#14BE77'
const OUT_RANGE = '#F44336'
const GREEN = '#0A905F'

function Content() {
  const [cidInput, setCidInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [patient, setPatient] = useState(null)
  const [visits, setVisits] = useState(null)
  const [selectedVisit, setSelectedVisit] = useState(null)
  const [selectedLabType, setSelectedLabType] = useState(null)

  const onSearch = async () => {
    const cId = String(cidInput || '').trim()
    if (!cId) {
      message.warning('กรุณากรอก cId ของคนไข้')
      return
    }
    setLoading(true)
    setPatient(null)
    setVisits(null)
    setSelectedVisit(null)
    setSelectedLabType(null)
    try {
      const infos = await getUserInfoByCid(cId)
      const info = Array.isArray(infos) ? infos[0] : null
      if (!info || !info.userId) {
        message.error('ไม่พบคนไข้ที่มี cId นี้')
        setLoading(false)
        return
      }
      const name = [info.firstName, info.lastName].filter(Boolean).join(' ')
      setPatient({ cId: info.cId, userId: info.userId, name })

      const data = await getVisitsByPatientId(info.userId, 1, 20)
      const docs = data && Array.isArray(data.docs) ? data.docs : []
      setVisits(processVisits(docs))
    } catch (e) {
      message.error('เกิดข้อผิดพลาดในการดึงข้อมูล')
    } finally {
      setLoading(false)
    }
  }

  const renderVisitList = () => {
    if (!visits) {
      return null
    }
    if (visits.length === 0) {
      return <Empty description="ไม่พบประวัติการรักษา" />
    }
    return (
      <List
        dataSource={visits}
        rowKey={(visit) => visit.diagId}
        renderItem={(visit) => (
          <Card
            hoverable
            style={{ marginBottom: 12 }}
            onClick={() => setSelectedVisit(visit)}
          >
            <div style={{ color: GREEN, fontWeight: 'bold', marginBottom: 4 }}>
              {formatVisitDateTime(visit.visitDateTime)}
            </div>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#095C3E' }}>
              {visit.icd10ThaiName}
            </div>
            {visit.icd10Name ? (
              <div style={{ color: '#888', marginBottom: 6 }}>{visit.icd10Name}</div>
            ) : null}
            <div style={{ color: '#555' }}>แผนก: {visit.department || '-'}</div>
            <div style={{ color: '#555' }}>แพทย์: {visit.doctorName}</div>
            <div style={{ marginTop: 8 }}>
              <Tag color={visit.hasLab ? 'green' : 'default'}>
                {visit.hasLab ? 'มีผลแลป' : 'ไม่มีผลแลป'}
              </Tag>
              <Tag color={visit.hasDrug ? 'green' : 'default'}>
                {visit.hasDrug ? 'มีรายการยา' : 'ไม่มีรายการยา'}
              </Tag>
            </div>
          </Card>
        )}
      />
    )
  }

  const renderVisitDetail = (visit) => {
    const labGroups = groupLabResults(visit.labResults)
    return (
      <div>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedVisit(null)}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          กลับไปรายการ visit
        </Button>

        <Descriptions title={visit.icd10ThaiName} column={1} size="small" bordered>
          <Descriptions.Item label="วันและเวลา">
            {formatVisitDateTime(visit.visitDateTime)}
          </Descriptions.Item>
          <Descriptions.Item label="แผนก">{visit.department || '-'}</Descriptions.Item>
          <Descriptions.Item label="แพทย์">{visit.doctorName}</Descriptions.Item>
        </Descriptions>

        <h3 style={{ marginTop: 18 }}>ผลตรวจห้องปฏิบัติการ</h3>
        {labGroups.length ? (
          labGroups.map((group, i) => (
            <Button
              key={i}
              block
              type="primary"
              style={{ marginBottom: 8, textAlign: 'left', height: 'auto', padding: '10px 14px', whiteSpace: 'normal' }}
              onClick={() => setSelectedLabType(group)}
            >
              {group.type}
            </Button>
          ))
        ) : (
          <Empty description="ไม่มีผลตรวจจากห้องปฏิบัติการ" />
        )}

        {visit.billingItems && visit.billingItems.length ? (
          <React.Fragment>
            <h3 style={{ marginTop: 18 }}>รายการยา</h3>
            <List
              size="small"
              bordered
              dataSource={visit.billingItems}
              renderItem={(med, i) => (
                <List.Item>
                  <span>{i + 1}. {med.drugNondugName}</span>
                  <span>{med.qty}</span>
                </List.Item>
              )}
            />
          </React.Fragment>
        ) : null}
      </div>
    )
  }

  const renderLabTable = (labType) => {
    const rows = dedupeLabRows(labType.data)
    const columns = [
      { title: 'รายการ', dataIndex: 'name', key: 'name' },
      {
        title: 'ผลตรวจ',
        dataIndex: 'result',
        key: 'result',
        align: 'center',
        render: (text, record) => {
          const isInRange = checkResultInRange(record.result, record.ref)
          const color = isInRange === true ? IN_RANGE : isInRange === false ? OUT_RANGE : undefined
          return <span style={{ color, fontWeight: 'bold' }}>{text}</span>
        },
      },
      { title: 'เกณฑ์ปกติ', dataIndex: 'ref', key: 'ref', align: 'right' },
    ]
    return (
      <div>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedLabType(null)}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          กลับไปรายละเอียด visit
        </Button>
        <h3>{labType.type}</h3>
        <Table
          columns={columns}
          dataSource={rows.map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          size="middle"
        />
      </div>
    )
  }

  const renderBody = () => {
    if (selectedLabType) {
      return renderLabTable(selectedLabType)
    }
    if (selectedVisit) {
      return renderVisitDetail(selectedVisit)
    }
    return renderVisitList()
  }

  return (
    <div style={{ padding: 24, maxWidth: 820, margin: '0 auto', width: '100%' }}>
      <h1 style={{ color: GREEN, marginBottom: 2 }}>ดูผลแลปคนไข้ (debug)</h1>
      <div style={{ color: '#888', marginBottom: 16 }}>
        กรอก cId ของคนไข้เพื่อดึงประวัติการรักษาและผลแลปแบบเดียวกับฝั่งมือถือ
      </div>

      <Input.Search
        placeholder="cId ของคนไข้ (เช่น 3101501761939)"
        enterButton={<span><SearchOutlined /> ค้นหา</span>}
        size="large"
        value={cidInput}
        onChange={(e) => setCidInput(e.target.value)}
        onSearch={onSearch}
        style={{ marginBottom: 16 }}
      />

      {patient ? (
        <div
          style={{
            background: '#EAF7F1',
            padding: '8px 12px',
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          คนไข้: {patient.name || '-'} | cId: {patient.cId} | userId: {patient.userId}
        </div>
      ) : null}

      <Spin spinning={loading}>{renderBody()}</Spin>
    </div>
  )
}

function PatientLabLookup(props) {
  return <Layout {...props} render={() => <Content />} />
}

export default PatientLabLookup
